import type { Directive } from 'vue'
import { SENSITIVE_URL_PARAMS, withoutQueryParamsDeep } from '@web/common/url'

/**
 * `v-safe-href="url"` — ссылка, в href которой нет токена и идентичности
 * посетителя, а переход идёт по полному адресу.
 *
 * Зачем: Метрика (trackLinks, вебвизор) и GA4 (outbound clicks) читают href
 * ссылок на странице сами, мимо Analytics.send. Если в href лежит адрес с
 * token или _ac_cid, они уходят в счётчики. Поэтому в DOM — чистый адрес, а
 * полный подставляем в момент клика (MOB-586).
 *
 * Тот же полный адрес обычно лежит и в data-analytics-element-target — вебвизор
 * записывает атрибуты DOM, поэтому чистим и его. Хук updated вызывается после
 * того, как Vue обновил атрибуты, так что очистка не перетирается.
 *
 * Клик, отменённый обработчиком компонента (preventDefault), не трогаем.
 * Ctrl/Cmd/Shift-клик и клик колёсиком открывают полный адрес в новой вкладке.
 * «Копировать ссылку» и «Открыть в новой вкладке» из контекстного меню дают
 * чистый адрес — без токена, это осознанно.
 */
interface SafeHrefState {
  full: string
  onClick: (event: MouseEvent) => void
  onAuxClick: (event: MouseEvent) => void
}

type SafeHrefElement = HTMLElement & { __safeHref?: SafeHrefState }

const ANALYTICS_TARGET_ATTR = 'data-analytics-element-target'

function apply (el: SafeHrefElement, value: unknown): void {
  const full = typeof value === 'string' ? value : ''
  if (el.__safeHref) {
    el.__safeHref.full = full
  }
  el.setAttribute('href', withoutQueryParamsDeep(full, SENSITIVE_URL_PARAMS))

  const analyticsTarget = el.getAttribute(ANALYTICS_TARGET_ATTR)
  if (analyticsTarget) {
    el.setAttribute(ANALYTICS_TARGET_ATTR, withoutQueryParamsDeep(analyticsTarget, SENSITIVE_URL_PARAMS))
  }
}

function needsInterception (el: SafeHrefElement, event: MouseEvent): string | null {
  const full = el.__safeHref?.full ?? ''
  if (event.defaultPrevented || !full || full === el.getAttribute('href')) {
    return null
  }
  return full
}

export const vSafeHref: Directive<SafeHrefElement, string | null | undefined> = {
  mounted (el, binding) {
    const state: SafeHrefState = {
      full: '',
      onClick: (event) => {
        const full = needsInterception(el, event)
        if (!full) return
        event.preventDefault()
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          window.open(full, '_blank', 'noopener')
          return
        }
        window.open(full, el.getAttribute('target') || '_self')
      },
      onAuxClick: (event) => {
        if (event.button !== 1) return
        const full = needsInterception(el, event)
        if (!full) return
        event.preventDefault()
        window.open(full, '_blank', 'noopener')
      }
    }
    el.__safeHref = state
    el.addEventListener('click', state.onClick)
    el.addEventListener('auxclick', state.onAuxClick)
    apply(el, binding.value)
  },
  updated (el, binding) {
    apply(el, binding.value)
  },
  beforeUnmount (el) {
    if (!el.__safeHref) return
    el.removeEventListener('click', el.__safeHref.onClick)
    el.removeEventListener('auxclick', el.__safeHref.onAuxClick)
    delete el.__safeHref
  }
}
